export const createTransferRow = (overrides = {}) => ({
    id: `transfer-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    item_code: "",
    item_name: "",
    stock_uom: "",
    qty: "",
    actual_qty: 0,
    valuation_rate: 0,
    has_serial_no: 0,
    has_batch_no: 0,
    serial_no: "",
    batch_no: "",
    ...overrides,
});

export const buildWarehouseOptions = (warehouses = []) =>
    warehouses
        .filter((warehouse) => warehouse?.name)
        .map((warehouse) => ({
            value: warehouse.name,
            label: warehouse.name,
        }));

export const buildItemOptions = (availableItems = [], selectedItems = []) => {
    const itemsByCode = new Map();

    // The shared endpoint returns variants in Item Attribute sequence/value
    // order. Preserve that order while retaining selected rows that are no
    // longer part of the current search result.
    [...availableItems, ...selectedItems].forEach((item) => {
        if (!item?.item_code) return;
        itemsByCode.set(item.item_code, {
            ...item,
            value: item.item_code,
            label: `${item.item_code}${
                item.item_name ? ` — ${item.item_name}` : ""
            } (${Number(item.actual_qty || 0).toLocaleString()} ${
                item.stock_uom || ""
            })`,
        });
    });

    return [...itemsByCode.values()];
};

const serialCount = (value) =>
    new Set(
        String(value || "")
            .replaceAll(",", "\n")
            .split("\n")
            .map((entry) => entry.trim())
            .filter(Boolean)
    ).size;

export const validateInterCompanyTransfer = (form, setup = {}) => {
    const errors = [];
    if (!form?.source_company) errors.push("selectSourceCompany");
    if (!form?.target_company) errors.push("selectTargetCompany");
    if (
        form?.source_company &&
        form.source_company === form.target_company
    ) {
        errors.push("companiesMustDiffer");
    }
    if (setup?.currency_compatible === false) {
        errors.push("currenciesMustMatch");
    }
    if (!form?.source_warehouse) errors.push("selectSourceWarehouse");
    if (!form?.target_warehouse) errors.push("selectTargetWarehouse");
    if (!form?.posting_date) errors.push("selectPostingDate");
    if (!form?.posting_time) errors.push("selectPostingTime");

    const rows = Array.isArray(form?.items) ? form.items : [];
    if (!rows.length) errors.push("addOneItem");
    const seen = new Set();
    rows.forEach((row, index) => {
        const rowNumber = index + 1;
        if (!row?.item_code) errors.push(["rowSelectItem", { row: rowNumber }]);
        if (row?.item_code && seen.has(row.item_code)) {
            errors.push(["rowDuplicateItem", { row: rowNumber }]);
        }
        if (row?.item_code) seen.add(row.item_code);

        const qty = Number(row?.qty);
        if (!Number.isFinite(qty) || qty <= 0) {
            errors.push(["rowPositiveQty", { row: rowNumber }]);
        } else if (Number(row?.actual_qty) < qty) {
            errors.push(["rowInsufficientStock", { row: rowNumber }]);
        }
        if (
            Number(row?.has_serial_no) === 1 &&
            Number.isFinite(qty) &&
            !Number.isInteger(qty)
        ) {
            errors.push(["rowWholeNumberQty", { row: rowNumber }]);
        }
        if (
            Number(row?.has_serial_no) === 1 &&
            row?.serial_no &&
            serialCount(row.serial_no) !== qty
        ) {
            errors.push(["rowSerialCount", { row: rowNumber }]);
        }
    });

    return errors;
};

export const buildInterCompanyTransferPayload = (form, requestId) => ({
    request_id: requestId,
    source_company: form.source_company,
    target_company: form.target_company,
    source_warehouse: form.source_warehouse,
    target_warehouse: form.target_warehouse,
    posting_date: form.posting_date,
    posting_time: form.posting_time,
    remarks: String(form.remarks || "").trim(),
    items: (form.items || []).map((row) => ({
        item_code: row.item_code,
        qty: Number(row.qty),
        batch_no: String(row.batch_no || "").trim(),
        serial_no: String(row.serial_no || "").trim(),
    })),
});

export const summarizeTransfer = (items = []) =>
    items.reduce(
        (summary, row) => {
            const qty = Number(row?.qty) || 0;
            summary.totalQty += qty;
            summary.totalValue += qty * (Number(row?.valuation_rate) || 0);
            return summary;
        },
        { totalQty: 0, totalValue: 0 }
    );
